import PageContainer from '../components/common/PageContainer';
import * as uiLayout from '../components/common/uiLayout';
import './rtl-forms-fix.css';
import { navigationContentSx } from '../config/sidebarLayout';
import NavigationShell from '../components/NavigationShell';
import React, { useEffect, useMemo, useState } from "react";
import {
  GlobalStyles,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import FolderZipRoundedIcon from "@mui/icons-material/FolderZipRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";

import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { SSTLI_API_URL } from "../config/apiUrls";





const API_BASE_URL = (
  SSTLI_API_URL
).replace(/\/+$/, "");

const PRIMARY = "#057445";
const PRIMARY_DARK = "#034f31";
const DANGER = "#8f171a";
const PAGE_BG = "#f4f7f5";
const BORDER = "#dbe7e1";
const TEXT = "#183128";
const MUTED = "#6d8178";
const WHITE = "#ffffff";

const getUserGuid = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return String(
      user?.guid ||
      user?.Guid ||
      user?.userGuid ||
      user?.UserGuid ||
      user?.USER_GUID ||
      localStorage.getItem("userGuid") ||
      localStorage.getItem("UserGuid") ||
      ""
    ).trim();
  } catch {
    return "";
  }
};

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("ar-SA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const getFileIcon = (file, size = 34) => {
  const extension = String(file?.extension || "").toLowerCase();
  const contentType = String(file?.contentType || "").toLowerCase();

  if (extension === ".pdf" || contentType.includes("pdf")) {
    return <PictureAsPdfRoundedIcon sx={{ color: DANGER, fontSize: size }} />;
  }

  if (
    [".jpg", ".jpeg", ".png", ".webp"].includes(extension) ||
    contentType.startsWith("image/")
  ) {
    return <ImageRoundedIcon sx={{ color: "#3276b1", fontSize: size }} />;
  }

  // ملفات مضغوطة (ZIP / RAR / 7Z).
  if ([".zip", ".rar", ".7z"].includes(extension)) {
    return <FolderZipRoundedIcon sx={{ color: "#b26a00", fontSize: size }} />;
  }

  if ([".doc", ".docx", ".txt"].includes(extension)) {
    return <DescriptionRoundedIcon sx={{ color: PRIMARY, fontSize: size }} />;
  }

  return <InsertDriveFileRoundedIcon sx={{ color: "#667b72", fontSize: size }} />;
};

const swalOptions = {
  confirmButtonColor: PRIMARY,
  cancelButtonColor: DANGER,
  customClass: {
    popup: "swal-cairo-popup",
    title: "swal-cairo-title",
    htmlContainer: "swal-cairo-text",
    confirmButton: "swal-cairo-button",
  },
};

const showErrorAlert = (message) =>
  Swal.fire({
    icon: "error",
    title: "تعذر تحميل المحتوى",
    text: message || "حدث خطأ أثناء تحميل مكتبة المحتوى.",
    confirmButtonText: "حسنًا",
    ...swalOptions,
  });

const CircularsList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const surfaces = theme.palette.surfaces || {};
  const darkCard = surfaces.card || "#13251d";
  const darkSection = surfaces.section || "#172b22";
  const darkNested = surfaces.nested || "#1b3328";
  const darkHover = surfaces.hover || "#214333";
  const userGuid = getUserGuid();

  const [tree, setTree] = useState([]);
  const [activeTabGuid, setActiveTabGuid] = useState("");
  const [activeFolderGuid, setActiveFolderGuid] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadTree = async () => {
    if (!userGuid) {
      setTree([]);
      setError("تعذر تحديد المستخدم الحالي.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${API_BASE_URL}/api/circulars/view-tree/${encodeURIComponent(userGuid)}`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        }
      );

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          result?.message ||
            (response.status === 403
              ? "ليس لديك صلاحية عرض مكتبة المحتوى."
              : "تعذر تحميل مكتبة المحتوى.")
        );
      }

      const data = Array.isArray(result?.data) ? result.data : [];
      setTree(data);

      setActiveTabGuid((current) => {
        if (current && data.some((tab) => String(tab.guid) === String(current))) {
          return current;
        }
        return data[0]?.guid || "";
      });
    } catch (err) {
      console.error("Content library load error:", err);
      setTree([]);
      const message = err?.message || "تعذر تحميل مكتبة المحتوى.";
      setError(message);
      showErrorAlert(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeTab = useMemo(
    () => tree.find((tab) => String(tab.guid) === String(activeTabGuid)) || null,
    [tree, activeTabGuid]
  );

  const folders = useMemo(
    () => (Array.isArray(activeTab?.folders) ? activeTab.folders : []),
    [activeTab]
  );

  const activeFolder = useMemo(
    () =>
      folders.find(
        (folder) => String(folder.guid) === String(activeFolderGuid)
      ) || null,
    [folders, activeFolderGuid]
  );

  const folderPath = useMemo(() => {
    if (!activeFolder) return [];

    const byGuid = new Map(
      folders.map((folder) => [String(folder.guid), folder])
    );
    const path = [];
    const visited = new Set();
    let current = activeFolder;

    while (current && !visited.has(String(current.guid))) {
      path.unshift(current);
      visited.add(String(current.guid));
      const parentGuid = String(current.parentFolderGuid || "");
      current = parentGuid ? byGuid.get(parentGuid) || null : null;
    }

    return path;
  }, [folders, activeFolder]);

  const currentFolders = useMemo(() => {
    const parentGuid = String(activeFolder?.guid || "");

    return folders.filter((folder) => {
      const folderParentGuid = String(folder?.parentFolderGuid || "");
      return parentGuid
        ? folderParentGuid === parentGuid
        : !folderParentGuid;
    });
  }, [folders, activeFolder]);

  useEffect(() => {
    if (
      activeFolderGuid &&
      !folders.some((folder) => String(folder.guid) === String(activeFolderGuid))
    ) {
      setActiveFolderGuid("");
    }
  }, [folders, activeFolderGuid]);

  const visibleFolders = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) return currentFolders;

    return currentFolders.filter((folder) =>
      [folder?.name, folder?.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [currentFolders, searchText]);

  const visibleFiles = useMemo(() => {
    const files = Array.isArray(activeFolder?.files) ? activeFolder.files : [];
    const search = searchText.trim().toLowerCase();

    if (!search) return files;

    return files.filter((file) =>
      [file?.displayName, file?.originalFileName, file?.extension]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [activeFolder, searchText]);

  const changeTab = (_, value) => {
    setActiveTabGuid(value);
    setActiveFolderGuid("");
    setSearchText("");
  };

  const enterFolder = (folder) => {
    setActiveFolderGuid(folder.guid);
    setSearchText("");
  };

  const leaveFolder = () => {
    setActiveFolderGuid(activeFolder?.parentFolderGuid || "");
    setSearchText("");
  };

  const openFile = (fileGuid) => {
    if (!fileGuid || !userGuid) return;
    window.open(
      `${API_BASE_URL}/api/circulars/files/${encodeURIComponent(
        fileGuid
      )}/open?userGuid=${encodeURIComponent(userGuid)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const downloadFile = (fileGuid) => {
    if (!fileGuid || !userGuid) return;
    window.open(
      `${API_BASE_URL}/api/circulars/files/${encodeURIComponent(
        fileGuid
      )}/download?userGuid=${encodeURIComponent(userGuid)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <NavigationShell variant="standard" ><Box className="circulars-dark-root"
      dir="rtl"
      sx={{
        minHeight: "100vh",
        bgcolor: isDark ? theme.palette.background.default : PAGE_BG,
        fontFamily: "Cairo, Arial, sans-serif",
      }}
    >

      <GlobalStyles
        styles={{
          ...(isDark
            ? {
                ".circulars-dark-root": {
                  backgroundColor: `${theme.palette.background.default} !important`,
                  color: `${theme.palette.text.primary} !important`
                },

                ".circulars-dark-root .MuiPaper-root, .circulars-dark-root .MuiCard-root": {
                  backgroundColor: `${darkCard} !important`,
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  borderColor: "#67C99D !important",
                  boxShadow: "none !important"
                },
                ".circulars-dark-root .MuiPaper-root .MuiPaper-root": {
                  backgroundColor: `${darkSection} !important`
                },

                ".circulars-dark-root .MuiButton-root, .MuiDialog-paper .MuiButton-root, .MuiPopover-paper .MuiButton-root": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: "#9BE0C1 !important",
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important",
                  borderRadius: "9px !important"
                },
                ".circulars-dark-root .MuiButton-root:hover, .MuiDialog-paper .MuiButton-root:hover, .MuiPopover-paper .MuiButton-root:hover": {
                  backgroundColor: "transparent !important",
                  color: "#C9F2DF !important",
                  borderColor: "#67C99D !important",
                  boxShadow: "0 0 0 1px rgba(103,201,157,.16) !important"
                },
                ".circulars-dark-root .MuiButton-root.Mui-disabled, .MuiDialog-paper .MuiButton-root.Mui-disabled": {
                  backgroundColor: "transparent !important",
                  color: "rgba(155,224,193,.42) !important",
                  borderColor: "rgba(103,201,157,.34) !important"
                },

                ".circulars-dark-root .MuiIconButton-root, .MuiDialog-paper .MuiIconButton-root": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: "#9BE0C1 !important",
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important"
                },
                ".circulars-dark-root .MuiIconButton-root:hover, .MuiDialog-paper .MuiIconButton-root:hover": {
                  backgroundColor: "transparent !important",
                  color: "#C9F2DF !important"
                },

                ".circulars-dark-root .MuiChip-root, .MuiDialog-paper .MuiChip-root": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: "#9BE0C1 !important",
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important"
                },

                ".circulars-dark-root .MuiTabs-root, .MuiDialog-paper .MuiTabs-root": {
                  backgroundColor: `${darkSection} !important`,
                  backgroundImage: "none !important",
                  border: "1px solid #67C99D !important",
                  borderRadius: "10px !important"
                },
                ".circulars-dark-root .MuiTab-root, .MuiDialog-paper .MuiTab-root": {
                  backgroundColor: "transparent !important",
                  color: `${theme.palette.text.secondary} !important`
                },
                ".circulars-dark-root .MuiTab-root.Mui-selected, .MuiDialog-paper .MuiTab-root.Mui-selected": {
                  backgroundColor: "transparent !important",
                  color: "#9BE0C1 !important"
                },
                ".circulars-dark-root .MuiTabs-indicator, .MuiDialog-paper .MuiTabs-indicator": {
                  backgroundColor: "#67C99D !important",
                  height: "2px !important"
                },

                ".circulars-dark-root .MuiOutlinedInput-root, .MuiDialog-paper .MuiOutlinedInput-root, .MuiPopover-paper .MuiOutlinedInput-root": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`
                },
                ".circulars-dark-root .MuiOutlinedInput-notchedOutline, .MuiDialog-paper .MuiOutlinedInput-notchedOutline, .MuiPopover-paper .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#67C99D !important",
                  borderWidth: "1px !important"
                },
                ".circulars-dark-root .MuiInputLabel-root, .MuiDialog-paper .MuiInputLabel-root, .MuiPopover-paper .MuiInputLabel-root, .circulars-dark-root .MuiFormHelperText-root": {
                  color: `${theme.palette.text.secondary} !important`
                },
                ".circulars-dark-root .MuiInputLabel-root.Mui-focused, .MuiDialog-paper .MuiInputLabel-root.Mui-focused": {
                  color: "#9BE0C1 !important"
                },
                ".circulars-dark-root .MuiInputAdornment-root, .circulars-dark-root .MuiInputAdornment-root .MuiSvgIcon-root, .circulars-dark-root .MuiSelect-icon, .MuiDialog-paper .MuiSelect-icon": {
                  color: "#9BE0C1 !important"
                },

                ".circulars-dark-root .MuiAlert-root, .MuiDialog-paper .MuiAlert-root": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important"
                },
                ".circulars-dark-root .MuiAlert-icon, .circulars-dark-root .MuiCircularProgress-root, .MuiDialog-paper .MuiCircularProgress-root": {
                  color: "#67C99D !important"
                },

                ".circulars-dark-root .MuiDivider-root, .MuiDialog-paper .MuiDivider-root": {
                  borderColor: "#67C99D !important"
                },

                ".MuiDialog-paper": {
                  backgroundColor: `${darkCard} !important`,
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  border: "1px solid #67C99D !important",
                  boxShadow: "0 18px 50px rgba(2,18,12,.34) !important"
                },
                ".MuiDialogTitle-root": {
                  backgroundColor: `${darkSection} !important`,
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  borderBottom: "1px solid #67C99D !important"
                },
                ".MuiDialogContent-root": {
                  backgroundColor: `${darkCard} !important`,
                  color: `${theme.palette.text.primary} !important`
                },
                ".MuiDialogActions-root": {
                  backgroundColor: `${darkSection} !important`,
                  borderTop: "1px solid #67C99D !important"
                },

                ".MuiMenu-paper, .MuiPopover-paper, .MuiAutocomplete-paper": {
                  backgroundColor: `${darkSection} !important`,
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  border: "1px solid #67C99D !important"
                },
                ".MuiMenuItem-root, .MuiAutocomplete-option": {
                  backgroundColor: "transparent !important",
                  color: `${theme.palette.text.primary} !important`
                },
                ".MuiMenuItem-root:hover, .MuiAutocomplete-option:hover": {
                  backgroundColor: `${darkHover} !important`
                },
                ".MuiMenuItem-root.Mui-selected, .MuiAutocomplete-option[aria-selected='true']": {
                  backgroundColor: "transparent !important",
                  color: "#9BE0C1 !important"
                },

                ".swal2-popup": {
                  backgroundColor: `${darkCard} !important`,
                  backgroundImage: "none !important",
                  color: `${theme.palette.text.primary} !important`,
                  border: "1px solid #67C99D !important"
                },
                ".swal2-title, .swal2-html-container, .swal2-input-label": {
                  color: `${theme.palette.text.primary} !important`
                },
                ".swal2-confirm, .swal2-deny, .swal2-cancel, .swal-cairo-button": {
                  backgroundColor: "transparent !important",
                  backgroundImage: "none !important",
                  color: "#9BE0C1 !important",
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important"
                },
                ".swal2-input, .swal2-textarea, .swal2-select": {
                  backgroundColor: "transparent !important",
                  color: `${theme.palette.text.primary} !important`,
                  border: "1px solid #67C99D !important",
                  boxShadow: "none !important"
                }
              }
            : {})
        }}
      />

      

      <PageContainer
        component="main"
        sx={{
          minHeight: "100vh",
          width: "100%",
          boxSizing: "border-box",
          
          ...navigationContentSx
        }}
      >
        <Stack spacing={1.25} sx={{ width: "100%" }}>
          <Paper
            elevation={0}
            sx={{
              px: { xs: 1.5, md: 2 },
              py: 1.4,
              borderRadius: 2,
              border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
              bgcolor: isDark ? darkCard : WHITE,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
              spacing={1.25}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2.5,
                    bgcolor: isDark ? darkNested : "#e9f5ef",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <MenuBookRoundedIcon sx={{ color: PRIMARY, fontSize: 27 }} />
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: "Cairo", fontWeight: 950, color: isDark ? theme.palette.text.primary : TEXT }}
                  >
                    مكتبة المحتوى
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: "Cairo", color: isDark ? theme.palette.text.secondary : MUTED }}
                  >
                    الأقسام والمجلدات والملفات في مكان واحد
                  </Typography>
                </Box>
              </Stack>

              <Button
                variant="outlined"
                startIcon={<RefreshRoundedIcon />}
                onClick={loadTree}
                disabled={loading}
                sx={uiLayout.withUiSx({
                  fontFamily: "Cairo",
                  fontWeight: 900,
                  borderRadius: 2.5,
                  borderColor: isDark ? "#67C99D" : BORDER,
                  color: PRIMARY,
                  "& .MuiButton-startIcon": { ml: 0.7, mr: 0 },
                }, uiLayout.buttonSx)}
              >
                تحديث
              </Button>
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2, fontFamily: "Cairo" }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Paper
              elevation={0}
              sx={{
                minHeight: 360,
                borderRadius: 2,
                border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                display: "grid",
                placeItems: "center",
                bgcolor: isDark ? darkCard : WHITE,
              }}
            >
              <Stack alignItems="center" spacing={1.3}>
                <CircularProgress sx={{ color: PRIMARY }} />
                <Typography sx={{ fontFamily: "Cairo", color: isDark ? theme.palette.text.secondary : MUTED }}>
                  جاري تحميل مكتبة المحتوى...
                </Typography>
              </Stack>
            </Paper>
          ) : tree.length === 0 && !error ? (
            <Paper
              elevation={0}
              sx={{
                minHeight: 360,
                borderRadius: 2,
                border: `1px dashed ${isDark ? "#67C99D" : BORDER}`,
                display: "grid",
                placeItems: "center",
                bgcolor: isDark ? darkCard : WHITE,
                textAlign: "center",
                p: 3,
              }}
            >
              <Box>
                <FolderRoundedIcon sx={{ fontSize: 62, color: "#a9bbb3" }} />
                <Typography
                  variant="h6"
                  sx={{ mt: 1, fontFamily: "Cairo", fontWeight: 900, color: isDark ? theme.palette.text.primary : TEXT }}
                >
                  لا توجد أقسام حاليًا
                </Typography>
              </Box>
            </Paper>
          ) : (
            <>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                  bgcolor: isDark ? darkCard : WHITE,
                  overflow: "hidden",
                }}
              >
                <Tabs
                  value={activeTabGuid || false}
                  onChange={changeTab}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 52,
                    px: 0.75,
                    "& .MuiTab-root": {
                      minHeight: 52,
                      minWidth: 120,
                      px: 2,
                      fontFamily: "Cairo",
                      fontWeight: 900,
                      color: "#61756c",
                    },
                    "& .Mui-selected": {
                      color: `${PRIMARY} !important`,
                      bgcolor: isDark ? "transparent" : "#eef7f2",
                    },
                    "& .MuiTabs-indicator": {
                      height: 3,
                      bgcolor: isDark ? "transparent" : PRIMARY,
                      borderRadius: 2,
                    },
                  }}
                >
                  {tree.map((tab) => (
                    <Tab key={tab.guid} value={tab.guid} label={tab.name} />
                  ))}
                </Tabs>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                  bgcolor: isDark ? darkCard : WHITE,
                  overflow: "hidden",
                  minHeight: 500,
                }}
              >
                <Box
                  sx={{
                    px: { xs: 1.5, md: 2 },
                    py: 1.3,
                    borderBottom: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                    bgcolor: isDark ? darkSection : "#fbfdfc",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    spacing={1.2}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.7}
                      sx={{ minWidth: 0 }}
                    >
                      {activeFolder ? (
                        <Button
                          onClick={leaveFolder}
                          startIcon={<ArrowBackRoundedIcon />}
                          sx={uiLayout.withUiSx({
                            minWidth: "auto",
                            px: 1,
                            color: PRIMARY,
                            fontFamily: "Cairo",
                            fontWeight: 900,
                            "& .MuiButton-startIcon": { ml: 0.4, mr: 0 },
                          }, uiLayout.buttonSx)}
                        >
                          رجوع
                        </Button>
                      ) : (
                        <HomeRoundedIcon sx={{ color: PRIMARY, fontSize: 22 }} />
                      )}

                      <Typography
                        sx={{
                          fontFamily: "Cairo",
                          color: isDark ? theme.palette.text.secondary : MUTED,
                          whiteSpace: "nowrap",
                        }}
                      >
                        مكتبة المحتوى
                      </Typography>

                      <Typography sx={{ color: "#a3b2ac" }}>/</Typography>

                      <Typography
                        sx={{
                          fontFamily: "Cairo",
                          fontWeight: 900,
                          color: activeFolder ? MUTED : TEXT,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {activeTab?.name || ""}
                      </Typography>

                      {folderPath.map((folder, index) => (
                        <React.Fragment key={folder.guid}>
                          <Typography sx={{ color: "#a3b2ac" }}>/</Typography>
                          <Button
                            onClick={() => {
                              setActiveFolderGuid(folder.guid);
                              setSearchText("");
                            }}
                            disabled={index === folderPath.length - 1}
                            sx={uiLayout.withUiSx({
                              minWidth: 0,
                              maxWidth: { xs: 110, sm: 180 },
                              px: 0.35,
                              color:
                                index === folderPath.length - 1
                                  ? TEXT
                                  : MUTED,
                              fontFamily: "Cairo",
                              fontWeight:
                                index === folderPath.length - 1 ? 950 : 800,
                              justifyContent: "flex-start",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }, uiLayout.buttonSx)}
                          >
                            {folder.name}
                          </Button>
                        </React.Fragment>
                      ))}
                    </Stack>

                    <TextField InputLabelProps={{ shrink: true }}
                      size="small"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      placeholder={
                        activeFolder
                          ? "ابحث في المجلدات والملفات..."
                          : "ابحث في المجلدات..."
                      }
                      sx={uiLayout.withUiSx({
                        width: { xs: "100%", md: 330 },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2.5,
                          bgcolor: isDark ? darkCard : WHITE,
                        },
                        "& input": { fontFamily: "Cairo" },
                      }, uiLayout.formFieldSx)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: "#82968d" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  {activeFolder ? (
                    <>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 1.5 }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2.5,
                              bgcolor: isDark ? darkNested : "#fff7dc",
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            <FolderOpenRoundedIcon
                              sx={{ color: "#c89512", fontSize: 31 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontFamily: "Cairo",
                                fontWeight: 950,
                                color: isDark ? theme.palette.text.primary : TEXT,
                                lineHeight: 1.35,
                              }}
                            >
                              {activeFolder.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: "Cairo", color: isDark ? theme.palette.text.secondary : MUTED }}
                            >
                              {currentFolders.length} مجلد فرعي •{" "}
                              {Array.isArray(activeFolder.files)
                                ? activeFolder.files.length
                                : 0}{" "}
                              ملف
                            </Typography>
                          </Box>
                        </Stack>
                      </Stack>

                      {activeFolder.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1.5,
                            fontFamily: "Cairo",
                            color: isDark ? theme.palette.text.secondary : MUTED,
                          }}
                        >
                          {activeFolder.description}
                        </Typography>
                      )}
                    </>
                  ) : (
                    activeTab?.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.5,
                          fontFamily: "Cairo",
                          color: isDark ? theme.palette.text.secondary : MUTED,
                        }}
                      >
                        {activeTab.description}
                      </Typography>
                    )
                  )}

                  {visibleFolders.length > 0 && (
                    <Box sx={{ mb: activeFolder ? 2 : 0 }}>
                      {activeFolder && (
                        <Typography
                          sx={{
                            mb: 1,
                            fontFamily: "Cairo",
                            fontWeight: 950,
                            color: isDark ? theme.palette.text.primary : TEXT,
                          }}
                        >
                          المجلدات الفرعية
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            sm: "repeat(3, minmax(0, 1fr))",
                            md: "repeat(4, minmax(0, 1fr))",
                            lg: "repeat(5, minmax(0, 1fr))",
                            xl: "repeat(6, minmax(0, 1fr))",
                          },
                          gap: 1.25,
                        }}
                      >
                        {visibleFolders.map((folder) => {
                          const fileCount = Array.isArray(folder.files)
                            ? folder.files.length
                            : 0;
                          const subFolderCount = folders.filter(
                            (candidate) =>
                              String(candidate?.parentFolderGuid || "") ===
                              String(folder.guid)
                          ).length;

                          return (
                            <Paper
                              key={folder.guid}
                              elevation={0}
                              role="button"
                              tabIndex={0}
                              onClick={() => enterFolder(folder)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") enterFolder(folder);
                              }}
                              sx={{
                                p: 1.5,
                                minHeight: 135,
                                borderRadius: 2,
                                border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                                bgcolor: isDark ? darkCard : "#fcfefd",
                                cursor: "pointer",
                                transition: "all .18s ease",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  borderColor: "#a9cdbb",
                                  boxShadow:
                                    "0 8px 24px rgba(5,116,69,0.09)",
                                  bgcolor: isDark ? darkCard : "#ffffff",
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                              >
                                <FolderRoundedIcon
                                  sx={{
                                    fontSize: 48,
                                    color: "#d7a429",
                                    filter:
                                      "drop-shadow(0 3px 4px rgba(0,0,0,.08))",
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`${subFolderCount} مجلد`}
                                  sx={{
                                    height: 23,
                                    fontFamily: "Cairo",
                                    fontWeight: 900,
                                    bgcolor: isDark ? "transparent" : "#edf5f1",
                                    color: PRIMARY,
                                  }}
                                />
                              </Stack>

                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  sx={{
                                    fontFamily: "Cairo",
                                    fontWeight: 950,
                                    color: isDark ? theme.palette.text.primary : TEXT,
                                    lineHeight: 1.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {folder.name}
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontFamily: "Cairo",
                                    color: isDark ? theme.palette.text.secondary : MUTED,
                                  }}
                                >
                                  {fileCount} ملف
                                </Typography>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {activeFolder && visibleFiles.length > 0 && (
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontFamily: "Cairo",
                          fontWeight: 950,
                          color: isDark ? theme.palette.text.primary : TEXT,
                        }}
                      >
                        الملفات
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(3, minmax(0, 1fr))",
                            xl: "repeat(4, minmax(0, 1fr))",
                          },
                          gap: 1.1,
                        }}
                      >
                        {visibleFiles.map((file) => (
                          <Paper
                            key={file.guid}
                            elevation={0}
                            sx={{
                              p: 1.3,
                              borderRadius: 2,
                              border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                              bgcolor: isDark ? darkCard : "#fcfefd",
                              transition: "all .18s ease",
                              "&:hover": {
                                borderColor: "#b4d0c2",
                                boxShadow: "0 7px 20px rgba(5,116,69,.07)",
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.1}
                              alignItems="center"
                            >
                              <Box
                                sx={{
                                  width: 48,
                                  height: 48,
                                  borderRadius: 2.5,
                                  bgcolor: isDark ? darkCard : WHITE,
                                  border: `1px solid ${isDark ? "#67C99D" : BORDER}`,
                                  display: "grid",
                                  placeItems: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {getFileIcon(file)}
                              </Box>

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Tooltip
                                  title={
                                    file.displayName ||
                                    file.originalFileName ||
                                    ""
                                  }
                                >
                                  <Typography
                                    sx={{
                                      fontFamily: "Cairo",
                                      fontWeight: 900,
                                      color: isDark ? theme.palette.text.primary : TEXT,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {file.displayName || file.originalFileName}
                                  </Typography>
                                </Tooltip>

                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  useFlexGap
                                  flexWrap="wrap"
                                  sx={{ mt: 0.25 }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: "Cairo", color: isDark ? theme.palette.text.secondary : MUTED }}
                                  >
                                    {formatBytes(file.fileSize)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontFamily: "Cairo", color: isDark ? theme.palette.text.secondary : MUTED }}
                                  >
                                    {formatDate(file.createdAt)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>

                            <Stack direction="row" spacing={0.7} sx={{ mt: 1.2 }}>
                              <Button
                                size="small"
                                variant={isDark ? "outlined" : "contained"}
                                startIcon={<OpenInNewRoundedIcon />}
                                onClick={() => openFile(file.guid)}
                                sx={uiLayout.withUiSx({
                                  flex: 1,
                                  minHeight: 34,
                                  borderRadius: 2.2,
                                  bgcolor: isDark ? "transparent" : PRIMARY,
                                  fontFamily: "Cairo",
                                  fontWeight: 900,
                                  boxShadow: "none",
                                  "&:hover": {
                                    bgcolor: isDark ? "transparent" : PRIMARY_DARK,
                                    boxShadow: "none",
                                  },
                                  "& .MuiButton-startIcon": {
                                    ml: 0.5,
                                    mr: 0,
                                  },
                                }, uiLayout.buttonSx)}
                              >
                                فتح
                              </Button>

                              <Tooltip title="تنزيل الملف">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => downloadFile(file.guid)}
                                  sx={uiLayout.withUiSx({
                                    minWidth: 42,
                                    borderRadius: 2.2,
                                    borderColor: isDark ? "#67C99D" : BORDER,
                                    color: PRIMARY,
                                  }, uiLayout.buttonSx)}
                                >
                                  <DownloadRoundedIcon fontSize="small" />
                                </Button>
                              </Tooltip>
                            </Stack>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {visibleFolders.length === 0 &&
                    (!activeFolder || visibleFiles.length === 0) && (
                      <Box sx={{ py: 8, textAlign: "center" }}>
                        {activeFolder ? (
                          <InsertDriveFileRoundedIcon
                            sx={{ fontSize: 56, color: "#b7c5bf" }}
                          />
                        ) : (
                          <FolderRoundedIcon
                            sx={{ fontSize: 58, color: "#b4c4bd" }}
                          />
                        )}
                        <Typography
                          sx={{
                            mt: 1,
                            fontFamily: "Cairo",
                            fontWeight: 850,
                            color: isDark ? theme.palette.text.secondary : MUTED,
                          }}
                        >
                          {searchText
                            ? "لا توجد مجلدات أو ملفات مطابقة للبحث."
                            : activeFolder
                              ? "هذا المجلد فارغ."
                              : "لا توجد مجلدات داخل هذا القسم."}
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Paper>
            </>
          )}
        </Stack>
      </PageContainer>

      <style>
        {`
          .swal-cairo-popup,
          .swal-cairo-title,
          .swal-cairo-text,
          .swal-cairo-button {
            font-family: Cairo, Arial, sans-serif !important;
          }

          .swal-cairo-popup {
            direction: rtl !important;
          }

          .swal-cairo-title,
          .swal-cairo-text {
            text-align: center !important;
          }
        `}
      </style>
    </Box></NavigationShell>
  );
};

export default CircularsList;
